import { ID_PATH_SEPARATOR } from './constants.js';
import type { SceneObject } from './types.js';

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

function randomChars(n: number): string {
  const bytes = new Uint8Array(n);
  globalThis.crypto.getRandomValues(bytes);
  let s = '';
  for (const b of bytes) s += ALPHABET[b % 36];
  return s;
}

/**
 * Makes a short stable id, such as `o7f3`, that is not in `taken`. Ids are
 * only unique inside one file; runtime ids add placement paths on top.
 */
export function newId(taken: ReadonlySet<string>, prefix = 'o'): string {
  for (let len = 3; ; len++) {
    for (let attempt = 0; attempt < 20; attempt++) {
      const id = prefix + randomChars(len);
      if (!taken.has(id)) return id;
    }
  }
}

export function joinIdPath(...segments: string[]): string {
  return segments.filter((s) => s !== '').join(ID_PATH_SEPARATOR);
}

export function splitIdPath(path: string): string[] {
  return path.split(ID_PATH_SEPARATOR);
}

/** Visits every object in a tree depth-first, parents before children. */
export function walkObjects(
  objects: readonly SceneObject[] | undefined,
  visit: (obj: SceneObject, parent: SceneObject | undefined, index: number) => void | false,
  parent?: SceneObject,
): void {
  if (!objects) return;
  objects.forEach((obj, index) => {
    if (visit(obj, parent, index) === false) return;
    const children = (obj as { children?: unknown }).children;
    if (Array.isArray(children)) walkObjects(children as SceneObject[], visit, obj);
  });
}

/** Every id in a tree, including duplicates, in visit order. */
export function listIds(objects: readonly SceneObject[] | undefined): string[] {
  const ids: string[] = [];
  walkObjects(objects, (o) => {
    ids.push(o.id);
  });
  return ids;
}

/** Finds an object by its id within one file. */
export function findById(objects: readonly SceneObject[] | undefined, id: string): SceneObject | undefined {
  let found: SceneObject | undefined;
  walkObjects(objects, (o) => {
    if (found) return false;
    if (o.id === id) found = o;
  });
  return found;
}
